/**
 * Deployment version of the running code.
 *
 * Why this exists (2026-07-26): `/admin/errors` mixed errors from deploys that
 * shipped weeks ago with errors in the code that is live right now, so the log
 * could not answer the only question that matters during triage — "is this
 * broken NOW?". Every row is now attributed to the deploy that produced it.
 *
 * The token is the short commit SHA, injected at BUILD time by Cloudflare
 * Workers Builds as `WORKERS_CI_COMMIT_SHA` and inlined into every bundle via
 * next.config's `env` key. See the `resolveAppVersion` doc-block in
 * `next.config.ts` for why it is deliberately NOT wired through
 * `generateBuildId` or `deploymentId` (both re-key OpenNext's incremental-cache
 * namespace, which is keyed on `OPEN_NEXT_BUILD_ID = deploymentId ?? BuildId`).
 *
 * NULL IS A SUPPORTED STATE, everywhere, on purpose. If the SHA is ever absent
 * — a renamed CI variable, a local `npm run deploy`, `next dev` — every caller
 * degrades to today's behaviour rather than breaking. Nothing here may ever be
 * made required: an unset build variable must not be able to fail a deploy, and
 * a filter that hides a real error is worse than the noise it removes.
 */

/**
 * Build-time LITERAL, substituted by webpack's DefinePlugin from next.config's
 * `env` key. Identical in the browser bundle, the Worker bundle and edge
 * middleware, so client and server can never disagree about what "the current
 * version" means.
 *
 * Do NOT destructure `process.env` here — DefinePlugin only substitutes the
 * full `process.env.NEXT_PUBLIC_BUILD_ID` member expression, so
 * `const { NEXT_PUBLIC_BUILD_ID } = process.env` silently compiles to
 * `undefined` in the browser.
 */
const INLINED = process.env.NEXT_PUBLIC_BUILD_ID || null;

/** Max stored length — also the cap applied to untrusted reported versions. */
export const MAX_VERSION_LEN = 64;

/**
 * Version the RUNNING BUNDLE was built from. Safe on client and server.
 *
 * On the client this is the whole story: a tab still executing a pre-deploy
 * bundle reports the OLD version, which is exactly the signal we want (see
 * `sanitizeReportedVersion`).
 */
export function appVersion(): string | null {
  return INLINED;
}

/**
 * Server-side version, with runtime fallbacks for when the inlined literal is
 * absent (no `WORKERS_CI_COMMIT_SHA` — e.g. a local disaster-recovery deploy).
 *
 *  1. the inlined literal — identical to what the browser reports;
 *  2. `process.env.NEXT_BUILD_ID`, set at isolate init by
 *     `@opennextjs/aws/dist/adapters/config/index.js`, which the Worker imports
 *     via `core/requestHandler.js`. Always present on Workers, never in
 *     `next dev`. (OpenNext marks it deprecated above Next 16.2, hence tier 2
 *     rather than tier 1.)
 *  3. `env.CF_VERSION_METADATA.id` — only if the optional `version_metadata`
 *     binding is added to wrangler.jsonc. Absent by default.
 *
 * Never throws; returns null when nothing is knowable.
 */
export async function serverAppVersion(): Promise<string | null> {
  if (INLINED) return INLINED;
  const buildId = process.env.NEXT_BUILD_ID;
  if (buildId) return buildId;
  try {
    const { getCloudflareContext } = await import("@opennextjs/cloudflare");
    const { env } = await getCloudflareContext({ async: true });
    const meta = (env as unknown as { CF_VERSION_METADATA?: { id?: string } })
      .CF_VERSION_METADATA;
    return meta?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Sanitise a version reported by an untrusted client.
 *
 * `POST /api/client-errors` is public and unauthenticated, so `body.buildId` is
 * forgeable. The server deliberately does NOT overwrite it with its own version
 * — a client running an OLD bundle across a deploy is precisely the case the
 * feature exists to surface, and stamping the server's version over it would
 * erase that signal and make every row read "current".
 *
 * The forgery ceiling is low: the worst a forged value achieves is hiding a row
 * from the DEFAULT view, while it stays in the table, stays counted in the
 * "N from earlier deployments" affordance, and is one click away. Anyone able
 * to POST a forged version can already do strictly worse by flooding the ingest
 * limiter. So: cap the length and charset, and record the disagreement rather
 * than resolving it.
 */
export function sanitizeReportedVersion(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!t || t.length > MAX_VERSION_LEN) return null;
  return /^[A-Za-z0-9._-]+$/.test(t) ? t : null;
}
